import React, { useState } from 'react';
import { usePopper } from 'react-popper';
import { styled } from 'twin.macro';

type QA = {
  question: string;
  answer: string;
};

type Props = {
  qa: QA;
};

function cleanHTMLQuestion(html: string): string {
  // 1. Parse string to DOM
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const container = doc.body;

  // 2. Unwrap ol, ul, li
  const listElements = container.querySelectorAll('ol, ul, li');

  listElements.forEach((el) => {
    const parent = el.parentNode;
    if (!parent) return;

    while (el.firstChild) {
      parent.insertBefore(el.firstChild, el);
    }

    parent.removeChild(el);
  });

  // 3. Force bold
  container.innerHTML = `<strong>${container.innerHTML}</strong>`;

  // 4. Return
  return container.innerHTML;
}

const QARevealContainer = styled.div`
  display: flex;
  align-items: flex-start;

  .dot {
    margin-right: 5px;
  }

  .popup {
    width: 340px;
    border-radius: 10px;
    background-color: #fff;
    border: 2px solid rgb(117, 135, 193);
    padding-left: 16px;
    padding-top: 16px;
    padding-bottom: 16px;
    padding-right: 34px;

    &::before {
      content: '';
      position: absolute;
      top: -37px;
      left: 50%;
      transform: translateX(-50%);

      border-width: 19px;
      border-style: solid;
      border-color: transparent transparent rgb(117, 135, 193) transparent;
    }

    &::after {
      content: '';
      position: absolute;
      top: -31px;
      left: 50%;
      transform: translateX(-50%);

      border-width: 16px;
      border-style: solid;
      border-color: transparent transparent #fff transparent;
    }

    &__close {
      position: absolute;
      right: 10px;
      top: 10px;

      img {
        width: 15px;
        height: 15px;
      }
    }

    &-button {
      margin-left: 10px;
      margin-top: 6px;
    }

    &__question {
      font-size: 16px;
      font-weight: bold;

      *:last-child {
        margin-bottom: 0;
      }
    }

    &__answer {
      font-size: 16px;
    }
  }

  ul {
    list-style-type: disc;
    padding-left: 16px;
  }

  ol {
    list-style-type: decimal;
    padding-left: 22px;
  }
`;

const QAReveal: React.FC<Props> = ({ qa: { question, answer } }) => {
  const [referenceElement, setReferenceElement] =
    useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );
  const [show, setShow] = useState(false);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [1, 18],
        },
      },
    ],
  });

  return (
    <QARevealContainer>
      <div
        className="question"
        dangerouslySetInnerHTML={{
          __html: question,
        }}
      />
      <button
        className="popup-button"
        ref={setReferenceElement}
        onClick={() => setShow(true)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 14 14"
          id="Lightbulb--Streamline-Flex-Remix"
          height="20"
          width="20"
        >
          <g id="lightbulb--lighting-light-incandescent-bulb-lights">
            <path
              id="Union"
              fill="#7587c1"
              fillRule="evenodd"
              d="M7 0C5.48509 0 4.21327 0.427647 3.32073 1.31948 2.42812 2.21138 2 3.48246 2 4.99652c0 0.0095 0.00022 0.01899 0.00065 0.02848 0.03966 0.86972 0.33156 1.91851 0.82658 2.67698 0.18027 0.2762 0.37074 0.53848 0.54752 0.78191l0.0576 0.07935c0.19663 0.27115 0.37354 0.51899 0.52582 0.76834 0.29994 0.49114 0.48038 0.95062 0.48038 1.48542 0 0.0154 0.00056 0.0306 0.00165 0.0458 -0.00175 0.0743 -0.0027 0.1492 -0.0027 0.2247 0 0.4361 0.03184 0.8583 0.06765 1.2529l0.00029 0.003c0.03575 0.3739 0.17859 0.7366 0.42211 1.0248 0.24488 0.2899 0.58331 0.4926 0.96906 0.5452l0.00373 0.0005C6.23716 13.9577 6.6104 14 7 14c0.38949 0 0.76278 -0.0419 1.10109 -0.0867l0.0023 -0.0003c0.38576 -0.0526 0.72419 -0.2553 0.96907 -0.5451 0.24352 -0.2883 0.38636 -0.651 0.42211 -1.0249l0.00027 -0.0028c0.03583 -0.3941 0.06767 -0.8162 0.06767 -1.2527 0 -0.0756 -0.00095 -0.1505 -0.0027 -0.2249 0.00108 -0.0151 0.00164 -0.0303 0.00164 -0.0456 0 -0.5348 0.18045 -0.99428 0.48035 -1.48542 0.1523 -0.24935 0.3292 -0.49719 0.5259 -0.76834l0.0575 -0.07926c0.1768 -0.24345 0.3673 -0.50576 0.5476 -0.782 0.495 -0.75847 0.7869 -1.80727 0.8266 -2.67698 0.0004 -0.00949 0.0006 -0.01898 0.0006 -0.02848 0 -1.51406 -0.4281 -2.78514 -1.3207 -3.67704C9.78673 0.427647 8.51491 0 7 0Zm0.62528 9.99398c0.28837 0.01522 0.52741 0.04222 0.76447 0.08032 0.11176 -0.52246 0.33177 -0.97909 0.58528 -1.39421 0.18113 -0.29659 0.38504 -0.58089 0.5807 -0.85069l0.05672 -0.07815c0.17929 -0.24695 0.35129 -0.48387 0.51355 -0.73246 0.3522 -0.53965 0.5903 -1.35669 0.624 -2.03679 -0.0027 -1.27609 -0.3616 -2.18612 -0.95427 -2.77829C9.20078 1.60923 8.28509 1.25 7 1.25c-1.28509 0 -2.20078 0.35923 -2.79573 0.95371 -0.59264 0.59217 -0.95155 1.50221 -0.95425 2.7783 0.03373 0.68009 0.27179 1.49713 0.624 2.03678 0.16223 0.24857 0.3342 0.48545 0.51348 0.73239l0.05677 0.07822c0.19566 0.2698 0.39957 0.5541 0.5807 0.85069 0.2535 0.41511 0.47351 0.87172 0.58527 1.39421 0.2372 -0.0382 0.47641 -0.0651 0.76504 -0.08036V8.20857c-0.15318 -0.0822 -0.29461 -0.19656 -0.41512 -0.34311 -0.38011 -0.46224 -0.80973 -1.10292 -1.11261 -1.82571 -0.1334 -0.31836 0.01653 -0.68459 0.33489 -0.81799 0.31836 -0.1334 0.68458 0.01653 0.81799 0.33489 0.24585 0.5867 0.60395 1.12419 0.92521 1.51487 0.03294 0.04006 0.06123 0.04454 0.07496 0.04454s0.04202 -0.00448 0.07496 -0.04454c0.32127 -0.39068 0.67936 -0.92817 0.92521 -1.51487 0.13341 -0.31836 0.49963 -0.46829 0.81799 -0.33489 0.31836 0.1334 0.46829 0.49963 0.33489 0.81799 -0.30288 0.72279 -0.73249 1.36347 -1.11261 1.82571 -0.12067 0.14675 -0.26233 0.26123 -0.41576 0.34345v1.78507Z"
              clipRule="evenodd"
              strokeWidth="1"
            ></path>
          </g>
        </svg>
      </button>
      {show && (
        <div
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
          className="popup"
        >
          <button className="popup__close" onClick={() => setShow(false)}>
            <img src="/img/close-black.svg" alt="close" />
          </button>
          <div
            className="popup__question"
            dangerouslySetInnerHTML={{
              __html: cleanHTMLQuestion(question as string),
            }}
          />
          <div className="popup__answer">{answer}</div>
        </div>
      )}
    </QARevealContainer>
  );
};

export default QAReveal;
